import type { SupabaseClient } from "@supabase/supabase-js";

import type { UserRole } from "../types/domain";

/** Roles a tenant_admin / switched super_admin may assign from the tenant user module (governance doc). */
export const ASSIGNABLE_TENANT_STAFF_ROLES = ["supervisor", "finance", "operator", "legal"] as const;
export type AssignableTenantStaffRole = (typeof ASSIGNABLE_TENANT_STAFF_ROLES)[number];

export type GovernanceActorProfile = {
  id: string;
  role: string;
  tenant_id: string;
  platform_effective_tenant_id: string | null;
};

export function allowedAssignableTenantRoles(tenantType?: string | null): readonly AssignableTenantStaffRole[] {
  return tenantType === "LEGAL" ? ASSIGNABLE_TENANT_STAFF_ROLES : ASSIGNABLE_TENANT_STAFF_ROLES.filter((role) => role !== "legal");
}

/**
 * DE EFFECTIEVE-TENANTREGEL, EXACT ZOALS DE DATABASE HEM DRAAGT.
 *
 * Spiegel van `public.collectr_effectieve_tenant_regel(...)` (migratie
 * `20261324070000`). RLS blijft autoritair; dit bestaat zodat de schermen
 * DEZELFDE vraag stellen als de server beantwoordt.
 *
 * DE KERN, en de reden dat deze migratie er is: een `super_admin` ZONDER
 * gekozen platformcontext krijgt **geen tenant**. Tot 18-09-2026 viel zowel de
 * database als de browser hier terug op de THUISTENANT. Gemeten gevolg op
 * productie: de kop toonde "Platform" terwijl de dossierlijst, de
 * werkvoorraadteller en de knop "Nieuw dossier" gewoon in `collectr-internal`
 * werkten. Een terugval die een context verzint is erger dan geen antwoord.
 */
export function effectieveTenantRegel(
  role: string | null,
  gekozenTenantId: string | null,
  thuisTenantId: string | null,
): string | null {
  if (role === "super_admin") {
    return gekozenTenantId ?? null;
  }
  return thuisTenantId ?? null;
}

/** Mirrors `collectr_actor_tenant_id()` for client-side filtering and copy; RLS is authoritative. */
export function effectiveTenantIdForActor(p: GovernanceActorProfile): string | null {
  return effectieveTenantRegel(p.role, p.platform_effective_tenant_id, p.tenant_id);
}

/**
 * G1-04: effectieve tenant voor client-queries — één query-helper die
 * `collectr_actor_tenant_id()` spiegelt. Een `super_admin` met actieve
 * tenant-context krijgt de effectieve tenant i.p.v. de thuis-tenant, zodat
 * client-filters en RLS dezelfde tenant zien (RLS blijft autoritair).
 */
export async function fetchEffectiveTenantIdForUser(
  client: SupabaseClient,
  userId: string,
): Promise<string | null> {
  const { data, error } = await client
    .from("users")
    .select("tenant_id, role, platform_effective_tenant_id")
    .eq("id", userId)
    .maybeSingle();
  if (error || !data) {
    return null;
  }
  const row = data as {
    tenant_id: string | null;
    role: string | null;
    platform_effective_tenant_id: string | null;
  };
  return effectieveTenantRegel(row.role, row.platform_effective_tenant_id, row.tenant_id);
}

/**
 * Who may open tenant user management routes and mutate staff users.
 * super_admin only when explicitly switched (`platform_effective_tenant_id` set).
 */
export function mayManageTenantUsers(role: UserRole | null, p: GovernanceActorProfile | null): boolean {
  if (!role || !p) return false;
  if (role === "tenant_admin") return true;
  if (role === "super_admin" && p.platform_effective_tenant_id != null) return true;
  return false;
}

/** Betaalproviderconfiguratie voor een tenant; gewone operators mogen deze niet beheren. */
export function mayConfigurePaymentIntegration(role: UserRole | null): boolean {
  if (!role) return false;
  return role === "tenant_admin" || role === "supervisor";
}

/** TrueLayer / open banking Slice 1 UI (internal); mirrors Edge `gateOpenBankingActor`. */
export function mayManageOpenBankingIntegration(role: UserRole | null, p: GovernanceActorProfile | null): boolean {
  if (!role || !p) return false;
  if (role === "tenant_admin" || role === "supervisor") return true;
  if (role === "super_admin" && p.platform_effective_tenant_id != null) return true;
  return false;
}

/** Payment reconciliation / Betalingen execution surface; mirrors Edge `gatePaymentReconciliationMutations`. */
export function mayManagePaymentReconciliation(role: UserRole | null, p: GovernanceActorProfile | null): boolean {
  if (!role || !p) return false;
  if (role === "tenant_admin" || role === "supervisor" || role === "finance" || role === "operator") return true;
  if (role === "super_admin" && p.platform_effective_tenant_id != null) return true;
  return false;
}

export function isAssignableStaffRole(role: string): role is AssignableTenantStaffRole {
  return (ASSIGNABLE_TENANT_STAFF_ROLES as readonly string[]).includes(role);
}

export function tenantUserRowIsMutable(role: string): boolean {
  return isAssignableStaffRole(role);
}
